/**
 * List Refresh Events Service
 * A lightweight event system to notify list components when data changes.
 *
 * Usage:
 * - Pages subscribe: listRefreshEvents.subscribe('residents', fetchResidents)
 * - After updates: listRefreshEvents.emit('residents')
 */

export type RefreshEventType =
  | 'people'
  | 'residents'
  | 'visitors'
  | 'employees'
  | 'condo-employees'
  | 'service_providers'
  | 'vehicles'
  | 'units'
  | 'blocks'
  // A person's credential changed on a device (sync, revoke, tag write). Several views
  // answer "is this person on that reader" from their own snapshot; without this they
  // drift and contradict each other after any sync.
  | 'device-credentials';

type RefreshListener = () => void | Promise<void>;

class ListRefreshEvents {
  private listeners: Map<RefreshEventType, Set<RefreshListener>> = new Map();

  /**
   * Subscribe to refresh events for a specific list type.
   * @param type - The type of list to subscribe to
   * @param listener - Function to call when refresh is triggered
   * @returns Unsubscribe function
   */
  subscribe(type: RefreshEventType, listener: RefreshListener): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.get(type)?.delete(listener);
    };
  }

  /**
   * Emit a refresh event to notify all subscribed listeners.
   * Also emits 'people' for any person-related refresh since they all appear in the People page.
   * @param type - The type of list to refresh
   */
  emit(type: RefreshEventType): void {
    // Emit to specific type listeners
    this.listeners.get(type)?.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error(`Error in list refresh listener for ${type}:`, error);
      }
    });

    // Also emit 'people' for any person-related refresh (they all show in People page)
    if (type !== 'people' && ['residents', 'visitors', 'employees', 'service_providers'].includes(type)) {
      this.listeners.get('people')?.forEach(listener => {
        try {
          listener();
        } catch (error) {
          console.error('Error in list refresh listener for people:', error);
        }
      });
    }
  }

  /**
   * Clear all listeners (useful for testing)
   */
  clear(): void {
    this.listeners.clear();
  }
}

export const listRefreshEvents = new ListRefreshEvents();
